import { FC, useMemo } from "react";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { getGenericErrorMessage } from "@app/shared/utility/getGenericErrorMessage";
import { QESWidget, QESWidgetContent, QESWidgetHeader, QESWidgetTitle } from "@app/shared/widget";
import { useTopFiveCountySpecialties } from "@app/compliance-connect/regulator/hooks/useTopFiveCountySpecialties";
import { wafflesKeyColumnName, ColumnConfig, TextAlignment } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { WafflesThemes } from "@app/shared/waffles/exports";

interface TopCountieSpecialtiesTableData {
  countyName: string;
  specialtyName: string;
  numberOfGaps: number;
  numberOfExceptions: number;
  numberOfIssuers: number;
  [wafflesKeyColumnName]: string;
}

const columnConfig: ColumnConfig = {
  /* oxlint-disable sort-keys */
  "countyName": {
    alignment: TextAlignment.Left,
    displayText: "County Name",
    noSort: true,
    testId: "county-name",
  },
  "specialtyName": {
    alignment: TextAlignment.Left,
    displayText: "Specialty Name",
    noSort: true,
    testId: "specialty-name",
  },
  "numberOfGaps": {
    alignment: TextAlignment.Right,
    displayText: "# of Gaps",
    noSort: true,
    testId: "number-of-gaps",
  },
  "numberOfExceptions": {
    alignment: TextAlignment.Right,
    displayText: "# of Exceptions",
    noSort: true,
    testId: "number-of-exceptions",
  },
  "numberOfIssuers": {
    alignment: TextAlignment.Right,
    displayText: "# of Issuers",
    noSort: true,
    testId: "number-of-issuers",
  }
};

const ComplianceConnectTopCountySpecialtyWidget: FC = () => {
    const { topFiveCountySpecialties, topFiveCountySpecialtiesError, isLoading: loading } = useTopFiveCountySpecialties();

    /* oxlint-disable sort-keys */
    const tableRows: TopCountieSpecialtiesTableData[] = useMemo(() => {
      return (topFiveCountySpecialties || []).map((row) => {
        return {
          countyName: row.countyName,
          specialtyName: row.specialtyName,
          numberOfGaps: row.numberOfGaps,
          numberOfExceptions: row.numberOfExceptions,
          numberOfIssuers: row.numberOfIssuers,
          [wafflesKeyColumnName]: row.countyName,
        };
      })
    }, [topFiveCountySpecialties]);
    /* oxlint-enable */

    const renderContent = () => {
        if (topFiveCountySpecialtiesError) return <div data-testid="compliance-connect-top-county-specialty-error"><QESAlert type="danger" persist>{getGenericErrorMessage()}</QESAlert></div>;
        return (
            <Waffles<TopCountieSpecialtiesTableData>
              columns={columnConfig}
              data={tableRows}
              disableStickyHeader={true}
              fullWidth={true}
              keyColumns={["countyName"]}
              loading={loading}
              testId="top-counties-by-gap"
              theme={WafflesThemes.ComplianceConnect}
            />
        );
    };

    return (
        <QESWidget className="compliance-connect-top-county-specialty-widget">
            <QESWidgetHeader>
                <QESWidgetTitle>TOP 5 COUNTY SPECIALTY COMBINATIONS BY ISSUER EXCEPTION COUNT</QESWidgetTitle>
            </QESWidgetHeader>
            <QESWidgetContent>
                {renderContent()}
            </QESWidgetContent>
        </QESWidget>
    );
};

export default ComplianceConnectTopCountySpecialtyWidget;
